import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import axios from 'axios';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ProductionDeclarationScrollToTopButton } from '@/features/production-declaration/components/shared/production-declaration-scroll-to-top-button';
import { ProductionDeclarationMaterialCommodityOrigins as CommodityOrigins } from '@/features/production-declaration/components/statement/production-declaration-material-commodity-origins';
import { ProductionDeclarationMaterialProductsList as ProductsList } from '@/features/production-declaration/components/statement/production-declaration-material-products-list';
import { useCanManageSubmittedProductionDeclaration } from '@/features/production-declaration/hooks/use-can-manage-submitted-production-declaration';
import { ProductionDeclarationMaterialLayout as Layout } from '@/features/production-declaration/layouts/production-declaration-material-layout';
import { useGetProductionDeclaration } from '@/services/apis/queries/production-declaration/use-get-production-declaration';
import { useGetProductionDeclarationMaterialCommodityOrigins } from '@/services/apis/queries/production-declaration/use-get-production-declaration-material-commodity-origins';
import { useGetProductionDeclarationMaterialProducts } from '@/services/apis/queries/production-declaration/use-get-production-declaration-material-products';
import { useGetProductionDeclarationMaterials } from '@/services/apis/queries/production-declaration/use-get-production-declaration-materials';

function isNotFoundError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 404;
}

export function ProductionDeclarationMaterialPage() {
  const navigate = useNavigate();
  const { id, materialId } = useParams({ strict: false });
  const { token } = useSearch({ strict: false });
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { t: tCommon } = useTranslation('common', {
    useSuspense: false,
  });
  const productionDeclarationId = `${id}`;
  const hasHandledMaterialNotFoundRef = useRef(false);
  const { data: declaration } = useGetProductionDeclaration(
    productionDeclarationId,
    token,
  );
  const { data: materials = [] } = useGetProductionDeclarationMaterials(
    productionDeclarationId,
    token,
  );
  const canComment = useCanManageSubmittedProductionDeclaration(declaration);
  const selectedMaterial = materials.find(
    (material) => material.id === materialId,
  );

  const {
    data: materialProducts = [],
    isLoading: isMaterialProductsLoading,
    error: materialProductsError,
  } = useGetProductionDeclarationMaterialProducts(
    productionDeclarationId,
    materialId,
    token,
  );

  const {
    data: commodityOrigins = [],
    isLoading: isCommodityOriginsLoading,
    error: commodityOriginsError,
  } = useGetProductionDeclarationMaterialCommodityOrigins(
    productionDeclarationId,
    materialId,
    token,
  );

  const hasMaterialNotFoundError =
    isNotFoundError(materialProductsError) ||
    isNotFoundError(commodityOriginsError);

  useEffect(() => {
    if (!hasMaterialNotFoundError || hasHandledMaterialNotFoundRef.current) {
      return;
    }

    hasHandledMaterialNotFoundRef.current = true;

    toast.error(tCommon('error'), {
      description: t('materials.notFound'),
    });

    navigate({
      params: { id: productionDeclarationId },
      replace: true,
      search: { token },
      to: '/production-declarations/$id/statement',
    }).catch(() => undefined);
  }, [
    hasMaterialNotFoundError,
    navigate,
    productionDeclarationId,
    t,
    tCommon,
    token,
  ]);

  if (hasMaterialNotFoundError) {
    return null;
  }

  return (
    <Layout>
      <ProductsList
        isLoading={isMaterialProductsLoading}
        products={materialProducts}
      />
      <CommodityOrigins
        canComment={canComment}
        commodityOrigins={commodityOrigins}
        isLoading={isCommodityOriginsLoading}
        materialId={materialId}
        materialStatusCode={selectedMaterial?.status.code}
        productionDeclarationId={productionDeclarationId}
        statusCode={declaration.status.code}
        token={token}
      />
      <ProductionDeclarationScrollToTopButton />
    </Layout>
  );
}
